import { useState } from 'react';
import { isTerminalMatterStatus } from '@/lib/matterStatus';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CalendarBlank,
  Buildings,
  User,
  Gavel,
  CircleNotch as Loader2,
  FileText,
  ChatTeardropDots,
  Receipt,
  Clock,
  CaretRight,
  DownloadSimple,
  Check,
  ClockCounterClockwise,
} from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  fetchMyMatter,
  fetchMyDocuments,
  fetchMyThreads,
  fetchMyFeeNotes,
  downloadSolicitorDocument,
  fetchMyMatterTimeline,
  type PortalDocument,
  type PortalThread,
  type PortalFeeNote,
  type PortalTimelineEvent,
} from '@/lib/solicitorPortalApi';
import { toast } from 'sonner';
import { SolicitorUploadModal } from '@/components/solicitor/SolicitorUploadModal';

type Tab = 'overview' | 'documents' | 'messages' | 'fees' | 'timeline';

export default function MatterDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<Tab>('overview');

  const { data: matter, isLoading, isError, error } = useQuery({
    queryKey: ['portal', 'matter', id],
    queryFn: () => fetchMyMatter(id!),
    enabled: !!id,
  });

  const { data: documents = [] } = useQuery({
    queryKey: ['portal', 'documents', id],
    queryFn: () => fetchMyDocuments(id!),
    enabled: !!id && tab === 'documents',
  });

  const { data: threads = [] } = useQuery({
    queryKey: ['portal', 'threads', id],
    queryFn: () => fetchMyThreads(id!),
    enabled: !!id && tab === 'messages',
  });

  const { data: allFeeNotes = [] } = useQuery({
    queryKey: ['portal', 'fee-notes'],
    queryFn: fetchMyFeeNotes,
    enabled: tab === 'fees',
  });

  // Filter fee notes for this matter
  const feeNotes = allFeeNotes.filter((fn) => fn.case.id === id);

  const { data: timeline = [], isLoading: timelineLoading } = useQuery({
    queryKey: ['portal', 'timeline', id],
    queryFn: () => fetchMyMatterTimeline(id!),
    enabled: !!id && tab === 'timeline',
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading matter details...
        </div>
      </div>
    );
  }

  if (isError || !matter) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-6 text-sm text-red-800 dark:text-red-300">
        {error instanceof Error ? error.message : 'Matter not found.'}
      </div>
    );
  }

  const lay = matter.layClient;
  const layName = lay
    ? lay.company ?? `${lay.firstName ?? ''} ${lay.lastName ?? ''}`.trim()
    : '—';

  const upcomingHearings = matter.hearings.filter(
    (h) => new Date(h.hearingDate) >= new Date()
  );

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/solicitor/matters"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        All matters
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-600 dark:text-brand-400">
            {matter.caseNumber}
          </div>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">
            {matter.caseName}
          </h1>
          {matter.description && (
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              {matter.description}
            </p>
          )}
        </div>
        <Badge
          variant={
            matter.status === 'active'
              ? 'success'
              : isTerminalMatterStatus(matter.status)
              ? 'outline'
              : 'default'
          }
          className="capitalize"
        >
          {matter.status.replace(/_/g, ' ')}
        </Badge>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {(
          [
            { key: 'overview', label: 'Overview', icon: Gavel },
            { key: 'timeline', label: 'Timeline', icon: ClockCounterClockwise },
            { key: 'documents', label: 'Documents', icon: FileText },
            { key: 'messages', label: 'Messages', icon: ChatTeardropDots },
            { key: 'fees', label: 'Fee Notes', icon: Receipt },
          ] as const
        ).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition ${
              tab === t.key
                ? 'border-brand-600 text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <div className="space-y-6">
          {/* Quick stats */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <InfoCard label="Court" value={matter.court?.name ?? '—'} />
            <InfoCard label="Practice area" value={matter.practiceArea?.name ?? '—'} />
            <InfoCard
              label="Opened"
              value={
                matter.openedDate
                  ? new Date(matter.openedDate).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : '—'
              }
            />
            <InfoCard
              label="Next hearing"
              value={
                upcomingHearings[0]
                  ? new Date(upcomingHearings[0].hearingDate).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                    })
                  : 'None scheduled'
              }
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Counsel */}
            <Panel title="Counsel">
              <div className="space-y-4">
                {matter.leadBarrister ? (
                  <Person
                    icon={<Gavel className="h-4 w-4" />}
                    role="Lead Barrister"
                    name={`${matter.leadBarrister.user.firstName} ${matter.leadBarrister.user.lastName}`}
                    tag={matter.leadBarrister.silkYear ? 'KC' : undefined}
                  />
                ) : (
                  <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                    <Clock className="h-4 w-4" />
                    Awaiting allocation
                  </div>
                )}
                {matter.juniorBarrister && (
                  <Person
                    icon={<Gavel className="h-4 w-4" />}
                    role="Junior Barrister"
                    name={`${matter.juniorBarrister.user.firstName} ${matter.juniorBarrister.user.lastName}`}
                  />
                )}
              </div>
            </Panel>

            {/* Parties */}
            <Panel title="Parties">
              <div className="space-y-4">
                <Person icon={<User className="h-4 w-4" />} role="Lay Client" name={layName} />
                <Person
                  icon={<Buildings className="h-4 w-4" />}
                  role="Instructing Firm"
                  name="Your firm"
                />
              </div>
            </Panel>
          </div>

          {/* Upcoming hearings */}
          <Panel title="Upcoming Hearings">
            {upcomingHearings.length === 0 ? (
              <div className="py-8 text-center">
                <CalendarBlank className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-2 text-sm text-muted-foreground">No hearings scheduled</p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {upcomingHearings.slice(0, 3).map((h) => {
                  const d = new Date(h.hearingDate);
                  return (
                    <li key={h.id} className="flex items-center gap-3 py-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600 dark:text-brand-400">
                        <CalendarBlank className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-foreground">
                          {h.hearingType.replace(/_/g, ' ')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {d.toLocaleDateString('en-GB', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                          })}
                          {' at '}
                          {d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      <Badge variant="info">{h.status}</Badge>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>

          {/* Quick actions */}
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm" onClick={() => setTab('documents')}>
              <FileText className="mr-1.5 h-4 w-4" />
              View Documents
            </Button>
            <Button variant="outline" size="sm" onClick={() => setTab('messages')}>
              <ChatTeardropDots className="mr-1.5 h-4 w-4" />
              Send Message
            </Button>
            <Button variant="outline" size="sm" onClick={() => setTab('fees')}>
              <Receipt className="mr-1.5 h-4 w-4" />
              View Fee Notes
            </Button>
          </div>
        </div>
      )}

      {tab === 'timeline' && (
        <TimelineTab events={timeline} loading={timelineLoading} />
      )}

      {tab === 'documents' && (
        <DocumentsTab documents={documents} matterId={id!} />
      )}

      {tab === 'messages' && (
        <MessagesTab threads={threads} matterId={id!} />
      )}

      {tab === 'fees' && (
        <FeesTab feeNotes={feeNotes} />
      )}
    </div>
  );
}

function DocumentsTab({ documents, matterId }: { documents: PortalDocument[]; matterId: string }) {
  const [uploadOpen, setUploadOpen] = useState(false);
  const qc = useQueryClient();

  const handleDownload = async (doc: PortalDocument) => {
    try {
      // Verified, authenticated download — server re-hashes the bytes before sending.
      await downloadSolicitorDocument(doc.id, doc.name);
    } catch {
      toast.error('Could not download document');
    }
  };

  if (documents.length === 0) {
    return (
      <Card className="py-12 text-center">
        <FileText className="mx-auto h-10 w-10 text-muted-foreground/40" />
        <p className="mt-3 text-sm font-medium text-foreground">No documents yet</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Documents shared by counsel will appear here.
        </p>
        {/* This had NO onClick — the strongest call-to-action in the portal's empty state,
            for the one class of user who is external to chambers and will not tolerate a dead
            button. The upload flow already existed on the portal's Documents page; it was
            simply never wired up here. */}
        <Button variant="primary" size="sm" className="mt-4" onClick={() => setUploadOpen(true)}>
          Upload a Brief
        </Button>
        {uploadOpen && (
          <SolicitorUploadModal
            matterId={matterId}
            onClose={() => setUploadOpen(false)}
            onUploaded={() => {
              setUploadOpen(false);
              qc.invalidateQueries({ queryKey: ['portal', 'documents'] });
              qc.invalidateQueries({ queryKey: ['portal', 'matter', matterId] });
            }}
          />
        )}
      </Card>
    );
  }

  return (
    <Card>
      <div className="divide-y divide-border">
        {documents.map((doc) => (
          <div
            key={doc.id}
            className="flex items-center gap-4 px-5 py-4 hover:bg-muted/30"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
              <FileText className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-foreground">{doc.name}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {doc.category} · {formatFileSize(Number(doc.fileSizeBytes))} ·{' '}
                {new Date(doc.createdAt).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                })}
              </div>
            </div>
            <Badge variant="outline">{doc.category}</Badge>
            <button
              onClick={() => handleDownload(doc)}
              className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <DownloadSimple className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function MessagesTab({ threads, matterId: _matterId }: { threads: PortalThread[]; matterId: string }) {
  if (threads.length === 0) {
    return (
      <Card className="py-12 text-center">
        <ChatTeardropDots className="mx-auto h-10 w-10 text-muted-foreground/40" />
        <p className="mt-3 text-sm font-medium text-foreground">No conversations yet</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Start a conversation with counsel about this matter.
        </p>
        <Link to="/solicitor/messages">
          <Button variant="primary" size="sm" className="mt-4">
            Start Conversation
          </Button>
        </Link>
      </Card>
    );
  }

  return (
    <Card>
      <div className="divide-y divide-border">
        {threads.map((thread) => {
          const otherParticipants = thread.participants
            .filter((p) => p.user.memberType !== 'solicitor')
            .map((p) => `${p.user.firstName} ${p.user.lastName}`)
            .join(', ');

          return (
            <Link
              key={thread.id}
              to={`/solicitor/messages?thread=${thread.id}`}
              className="flex items-center gap-4 px-5 py-4 hover:bg-muted/30"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-600 dark:text-brand-400">
                <ChatTeardropDots className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-foreground">
                  {(thread.subject ?? otherParticipants) || 'Conversation'}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {thread.participants.length} participants ·{' '}
                  {thread.lastMessageAt
                    ? formatRelativeTime(thread.lastMessageAt)
                    : 'No messages'}
                </div>
              </div>
              <CaretRight className="h-4 w-4 text-muted-foreground" />
            </Link>
          );
        })}
      </div>
    </Card>
  );
}

function FeesTab({ feeNotes }: { feeNotes: PortalFeeNote[] }) {
  if (feeNotes.length === 0) {
    return (
      <Card className="py-12 text-center">
        <Receipt className="mx-auto h-10 w-10 text-muted-foreground/40" />
        <p className="mt-3 text-sm font-medium text-foreground">No fee notes yet</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Fee notes from counsel will appear here when issued.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <table className="w-full">
        <thead>
          <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <th className="px-5 py-3">Number</th>
            <th className="px-5 py-3">Issued</th>
            <th className="px-5 py-3">Due</th>
            <th className="px-5 py-3 text-right">Total</th>
            <th className="px-5 py-3">Status</th>
            <th className="px-5 py-3 w-10"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {feeNotes.map((fn) => (
            <tr key={fn.id} className="hover:bg-muted/30">
              <td className="px-5 py-3 text-sm font-semibold text-brand-700 dark:text-brand-300">
                {fn.feeNoteNumber}
              </td>
              <td className="px-5 py-3 text-sm text-muted-foreground">
                {new Date(fn.issueDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                })}
              </td>
              <td className="px-5 py-3 text-sm text-muted-foreground">
                {new Date(fn.dueDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                })}
              </td>
              <td className="px-5 py-3 text-right text-sm font-semibold">
                £{Number(fn.total).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </td>
              <td className="px-5 py-3">
                <Badge
                  variant={
                    fn.status === 'paid'
                      ? 'success'
                      : fn.status === 'overdue'
                      ? 'danger'
                      : 'default'
                  }
                >
                  {fn.status === 'paid' && <Check className="mr-1 h-3 w-3" />}
                  {fn.status.replace(/_/g, ' ')}
                </Badge>
              </td>
              <td className="px-5 py-3">
                {fn.pdfUrl && (
                  <a
                    href={fn.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <DownloadSimple className="h-4 w-4" />
                  </a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-sm font-medium text-foreground">{value}</div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <div className="border-b border-border px-5 py-3 text-sm font-semibold text-foreground">
        {title}
      </div>
      <div className="px-5 py-4">{children}</div>
    </Card>
  );
}

function Person({
  icon,
  role,
  name,
  tag,
}: {
  icon: React.ReactNode;
  role: string;
  name: string;
  tag?: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {role}
        </div>
        <div className="text-sm font-medium text-foreground">
          {name}
          {tag && (
            <span className="ml-1.5 text-[10px] font-semibold uppercase text-brand-600 dark:text-brand-400">
              {tag}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRelativeTime(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

// ─── Timeline tab ──────────────────────────────────────────────

const TIMELINE_LABELS: Record<string, string> = {
  matter_created: 'Matter opened',
  matter_status_changed: 'Status updated',
  hearing_scheduled: 'Hearing scheduled',
  hearing_updated: 'Hearing updated',
  hearing_outcome_recorded: 'Hearing outcome recorded',
  conference_scheduled: 'Conference scheduled',
  conference_completed: 'Conference completed',
  document_uploaded: 'Document uploaded',
  fee_note_issued: 'Invoice issued',
  fee_note_paid: 'Invoice paid',
};

const TIMELINE_COLOURS: Record<string, string> = {
  hearing_outcome_recorded: 'bg-emerald-500',
  hearing_scheduled: 'bg-brand-500',
  document_uploaded: 'bg-violet-500',
  fee_note_issued: 'bg-amber-500',
  fee_note_paid: 'bg-emerald-600',
  matter_created: 'bg-brand-600',
};

function TimelineTab({
  events,
  loading,
}: {
  events: PortalTimelineEvent[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading timeline…
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <ClockCounterClockwise className="h-8 w-8 text-muted-foreground/30" weight="duotone" />
        <p className="text-sm font-medium text-muted-foreground">No timeline events yet</p>
        <p className="text-xs text-muted-foreground">Activity will appear here as the matter progresses.</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {events.map((event, idx) => {
        const label = TIMELINE_LABELS[event.eventType] ?? event.eventType.replace(/_/g, ' ');
        const dot = TIMELINE_COLOURS[event.eventType] ?? 'bg-muted-foreground/40';
        const date = new Date(event.occurredAt);
        const p = event.payload as Record<string, string | undefined>;

        return (
          <div key={event.id} className="flex gap-4 pb-1">
            {/* Spine */}
            <div className="flex flex-col items-center">
              <div className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${dot}`} />
              {idx < events.length - 1 && (
                <div className="mt-1 flex-1 w-px bg-border" />
              )}
            </div>
            {/* Content */}
            <div className="min-w-0 flex-1 pb-5">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-semibold text-foreground capitalize">{label}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
              {p.outcome && (
                <p className="mt-0.5 text-xs text-muted-foreground">{p.outcome}</p>
              )}
              {p.name && !p.outcome && (
                <p className="mt-0.5 text-xs text-muted-foreground">{p.name}</p>
              )}
              {p.newStatus && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Status: {p.newStatus.replace(/_/g, ' ')}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>

  );
}
